/**
 * Icon shim — lucide-compatible API backed by **Tabler Icons**.
 *
 * The `@icons` alias (vite.config.ts + tsconfig paths) points `from "lucide-react"`
 * here, so every call site stays identical: `<Search size={16} className="…" />`.
 *
 * №29.1 (③) — THIS USED TO BE MATERIAL SYMBOLS, and that is why it moved.
 *
 * Material Symbols arrived as a render-blocking stylesheet from
 * `fonts.googleapis.com` and could not be self-hosted: it is a LIGATURE-addressed
 * font — the span's text content IS the icon name — so every icon glyph is
 * reachable from an ordinary latin string and no subsetter can drop any of them.
 * Measured: Google's own `text=` subsetter returned 3,930,920 of 3,964,620 bytes
 * (0.8% saved); `pyftsubset` by glyph name kept 5,823 of 6,593 glyphs at 3.54MB.
 * Self-hosting it meant shipping 3.9MB to draw the 70 glyphs below.
 *
 * Tabler is already vendored, pinned (3.46.0) and same-origin — the App Shell has
 * used it for 388 call sites all along — so the maker console now draws from the
 * font the product already ships. That removes the last third-party stylesheet
 * from the document: nothing render-blocking, and nothing on a CDN we do not pin.
 *
 * Tabler is a STROKE font like lucide, where Material Symbols was a filled/variable
 * one, so the console's icons are marginally lighter than before. That is the
 * intended direction (the App Shell's icons are the reference), not a regression.
 *
 * To add an icon: map the lucide name to a Tabler icon name below. The name must
 * exist in `src/v3/styles/fonts.css` — `scripts/test-icon-names.ts` fails the build
 * if it does not, so a typo cannot ship as an invisible glyph.
 */

import type { CSSProperties } from "react";

interface IconProps {
  size?: number | string;
  className?: string;
  style?: CSSProperties;
  title?: string;
  /** Accepted for lucide API compatibility; the glyph carries its own weight. */
  strokeWidth?: number;
}

function makeIcon(name: string, spin = false) {
  function Icon({ size = 16, className = "", style, title }: IconProps) {
    return (
      <i
        className={`ti ti-${name}${spin ? " animate-spin" : ""}${className ? " " + className : ""}`}
        style={{ fontSize: typeof size === "number" ? `${size}px` : size, ...style }}
        title={title}
        aria-hidden={title ? undefined : true}
      />
    );
  }
  Icon.displayName = name;
  return Icon;
}

export const AlertTriangle = makeIcon("alert-triangle");
export const Archive = makeIcon("archive");
export const ArrowDown = makeIcon("arrow-down");
export const ArrowDownAZ = makeIcon("sort-a-z");
export const ArrowDownWideNarrow = makeIcon("arrow-narrow-down");
export const ArrowLeft = makeIcon("arrow-left");
export const ArrowRight = makeIcon("arrow-right");
export const ArrowUp = makeIcon("arrow-up");
export const Bot = makeIcon("robot");
export const BrainCircuit = makeIcon("brain");
export const CalendarDays = makeIcon("calendar-month");
export const Check = makeIcon("check");
export const ChevronDown = makeIcon("chevron-down");
export const ChevronLeft = makeIcon("chevron-left");
export const ChevronRight = makeIcon("chevron-right");
export const ChevronUp = makeIcon("chevron-up");
export const Clock = makeIcon("clock");
export const Code2 = makeIcon("code");
export const Compass = makeIcon("compass");
export const Copy = makeIcon("copy");
export const DollarSign = makeIcon("currency-dollar");
export const Download = makeIcon("download");
export const GripVertical = makeIcon("grip-vertical");
export const ExternalLink = makeIcon("external-link");
export const Eye = makeIcon("eye");
export const FileText = makeIcon("file-text");
export const Folder = makeIcon("folder");
export const FolderOpen = makeIcon("folder-open");
export const FolderUp = makeIcon("folder-up");
export const FolderPlus = makeIcon("folder-plus");
export const FolderTree = makeIcon("sitemap");
export const Home = makeIcon("home");
export const Layers = makeIcon("stack-2");
export const Link = makeIcon("link");
export const ListPlus = makeIcon("playlist-add");
export const Loader2 = makeIcon("loader-2", true);
export const LogOut = makeIcon("logout");
export const LayoutGrid = makeIcon("layout-grid");
export const Mail = makeIcon("mail");
export const MessageSquare = makeIcon("message");
export const MoreHorizontal = makeIcon("dots");
export const MoreVertical = makeIcon("dots-vertical");
export const Network = makeIcon("hierarchy-2");
export const PanelRightClose = makeIcon("layout-sidebar-right-collapse");
export const PanelRightOpen = makeIcon("layout-sidebar-right-expand");
export const Paperclip = makeIcon("paperclip");
export const Pause = makeIcon("player-pause");
export const Pencil = makeIcon("pencil");
export const Plug = makeIcon("plug");
export const Phone = makeIcon("phone");
export const Pin = makeIcon("pin");
export const PinOff = makeIcon("pinned-off");
export const Play = makeIcon("player-play");
export const Plus = makeIcon("plus");
export const RefreshCw = makeIcon("refresh");
export const RowsIcon = makeIcon("layout-rows");
export const Save = makeIcon("device-floppy");
export const Search = makeIcon("search");
export const Send = makeIcon("send");
export const Settings = makeIcon("settings");
export const Star = makeIcon("star");
export const Shield = makeIcon("shield");
export const Sparkles = makeIcon("sparkles");
export const Square = makeIcon("player-stop");
export const Table2 = makeIcon("table");
export const Trash2 = makeIcon("trash");
export const Upload = makeIcon("upload");
export const User = makeIcon("user");
export const Users = makeIcon("users");
export const Workflow = makeIcon("subtask");
export const X = makeIcon("x");
